/**
 * Broadcast "moments": sim events (and, as a fallback, HudSnapshot transitions) → super-titles, lower-thirds and
 * HUD pulses. The mapping functions are pure (moments.test.ts); `createMomentTracker` wires them to the store.
 *
 * Primary path: the match controller forwards each frame's de-duplicated events with `ui.ingestEvents(events)`.
 * Fallback path (no events ingested in this match): the play result is read from `HudSnapshot.lastResult` when a
 * play goes dead, so the HUD still celebrates touchdowns even before the event wiring exists.
 */
import { effect, signal } from '@preact/signals-core';
import { MatchFormat, specialInfo, type CharacterId } from '@hf/shared';
import { Phase, PlayResultKind, type HudSnapshot, type SimEvent } from '@hf/sim';
import { characterIdAt, displayName, firstDownFlashSub, halfLabel } from './format';
import type { UiStore } from './store';
import type { HudPulse, MatchSetup, SuperTitleExtras, SuperTitleTone } from './types';

export interface MomentContext {
  /** Display names per side ("THE ROOKIE"; mirror matches add "· P1"/"· CPU"). */
  names: [string, string];
  characters: [CharacterId, CharacterId];
  /**
   * Sides driven by a human on THIS screen (local slots). A lone human is addressed as "YOU" in coaching copy
   * (OFFSIDE: "YOU CROSSED BEFORE GO!"). Optional so hand-built contexts stay valid.
   */
  humans?: [boolean, boolean];
}

export type Moment =
  | ({
      kind: 'title';
      text: string;
      tone: SuperTitleTone;
      sub?: string;
      durationMs?: number;
    } & SuperTitleExtras)
  | { kind: 'lower'; title: string; sub?: string; tone: SuperTitleTone }
  | { kind: 'pulse'; pulse: HudPulse['kind']; player: number; text?: string };

export const contextFrom = (setup: MatchSetup | null, hud: HudSnapshot | null): MomentContext => {
  const characters: [CharacterId, CharacterId] = setup
    ? [setup.config.characters[0], setup.config.characters[1]]
    : hud
      ? [characterIdAt(hud.characters[0]), characterIdAt(hud.characters[1])]
      : ['rookie', 'bigtex'];
  const base: [string, string] = [displayName(characters[0]), displayName(characters[1])];
  const humans: [boolean, boolean] = [setup?.slots[0].kind === 'human', setup?.slots[1].kind === 'human'];
  if (characters[0] === characters[1]) {
    const tag = (i: 0 | 1): string => setup?.slots[i].name.toUpperCase() ?? (i === 0 ? 'P1' : 'P2');
    return { characters, humans, names: [`${base[0]} · ${tag(0)}`, `${base[1]} · ${tag(1)}`] };
  }
  return { characters, humans, names: base };
};

/** The one human of a 1-human match (vs CPU, online), else -1: coaching copy says "YOU" to that side. */
export const soloHuman = (ctx: MomentContext): 0 | 1 | -1 => {
  const h = ctx.humans;
  if (!h || h[0] === h[1]) return -1;
  return h[0] ? 0 : 1;
};

/**
 * OFFSIDE banner sub-line: says WHAT the defender did wrong (crossed the line before GO), not just the penalty.
 * The lone human reads it as "YOU …"; a CPU or one of two local players by name.
 */
export const offsideSub = (ctx: MomentContext, actor: number, penaltyYards: number | null): string => {
  const yds = penaltyYards === null ? 0 : Math.max(0, Math.round(penaltyYards));
  // "TRY", not "DOWN": Football 101 teaches 4 tries. Short enough for the super-title's sub pill.
  const pen = yds > 0 ? `${yds} YDS · REPLAY THE TRY` : 'REPLAY THE TRY';
  if (soloHuman(ctx) === side(actor)) return `YOU CROSSED BEFORE GO! · ${pen}`;
  return `${ctx.names[side(actor)]} CROSSED BEFORE GO · ${pen}`;
};

const side = (p: number): 0 | 1 => (p === 1 ? 1 : 0);
const other = (p: number): 0 | 1 => (p === 1 ? 0 : 1);
const scoreLine = (s: readonly [number, number] | readonly number[]): string => `${s[0] ?? 0} – ${s[1] ?? 0}`;

const head = (ctx: MomentContext, p: number, expression: string): SuperTitleExtras => ({
  character: ctx.characters[side(p)],
  expression,
  side: side(p),
});

const BAIT_NAMES: Record<string, string> = {
  fake_jump: 'FAKE JUMP',
  stutter: 'STUTTER STEP',
  hesitation: 'HESITATION',
  spin: 'SPIN MOVE',
  pump_fake: 'PUMP FAKE',
  hard_count: 'HARD COUNT',
  mirage: 'MIRAGE',
  fake_dive: 'FAKE DIVE',
  fake_rush: 'FAKE RUSH',
  fast_fall: 'FAST-FALL',
};

const MISS_REASON: Record<string, string> = {
  short: 'SHORT',
  over: 'OVER THE TOP',
  post: 'OFF THE POST',
  wide: 'WIDE',
};

/** Yardage lower-third for a tackle/sack result. */
export const yardageText = (yards: number): string =>
  yards > 0 ? `GAIN OF ${yards}` : yards < 0 ? `LOSS OF ${-yards}` : 'NO GAIN';

/** "THE ROOKIE SACKS BIG TOM · LOSS OF 3" (a sack loses at least 1: the sim's -0.4 is not "NO GAIN"). */
export const sackSub = (sacker: string, qb: string, yards: number): string =>
  `${sacker} SACKS ${qb} · LOSS OF ${Math.max(1, Math.round(Math.abs(yards)))}`;

/**
 * Overtime copy that matches the rules (college-style rounds: both sides get a possession from the 25, a lead after
 * the round wins). It used to say SUDDEN DEATH while the first TD still let the other side answer (QA r3).
 */
export const otSub = (round: number): string => `ROUND ${round} · BOTH SIDES GET A DRIVE`;

/** Card title for a recorded play: the passive "SACKED!" over the sacker's own face reads backwards: "SACK!". */
export const playTitle = (text: string): string => (text === 'SACKED!' ? 'SACK!' : text);

/** Map ONE sim event to broadcast moments. */
export const momentsFromEvent = (
  e: SimEvent,
  ctx: MomentContext,
  hud: HudSnapshot | null = null,
): Moment[] => {
  const name = (p: number): string => ctx.names[side(p)];
  switch (e.type) {
    case 'TOUCHDOWN':
      return [
        {
          kind: 'title',
          text: e.pickSix
            ? 'PICK SIX!!'
            : (e as { kickReturn?: boolean }).kickReturn
              ? 'RETURN TOUCHDOWN!!'
              : 'TOUCHDOWN!!',
          tone: e.pickSix ? 'cyan' : 'gold',
          sub: `${name(e.actor)} +6 · ${scoreLine(e.score)}`,
          stamp: '+6',
          durationMs: 2600,
          ...head(ctx, e.actor, 'celebrating'),
        },
      ];
    case 'FIELD_GOAL_GOOD':
      if (e.pat) return []; // conversions are announced by CONVERSION_RESULT
      return [
        {
          kind: 'title',
          text: 'FIELD GOAL IS GOOD!',
          tone: 'gold',
          sub: `${Math.round(e.distance)}-YD TOSS · ${scoreLine(e.score)}`,
          stamp: '+3',
          durationMs: 2200,
          ...head(ctx, e.actor, 'celebrating'),
        },
      ];
    case 'FIELD_GOAL_MISS':
      if (e.pat) return [];
      return [
        {
          kind: 'title',
          text: 'NO GOOD!',
          tone: 'red',
          sub: `${Math.round(e.distance)}-YD TOSS · ${MISS_REASON[e.reason] ?? 'MISSED'}`,
          durationMs: 1900,
          ...head(ctx, e.actor, 'shocked'),
        },
      ];
    case 'CONVERSION_RESULT':
      return [
        e.good
          ? {
              kind: 'title',
              text: e.points >= 2 ? '2-POINT GOOD!' : 'EXTRA POINT GOOD!',
              tone: 'gold',
              sub: `+${e.points} · ${scoreLine(e.score)}`,
              stamp: `+${e.points}`,
              size: 'pop',
              durationMs: 1700,
              ...head(ctx, e.actor, 'celebrating'),
            }
          : {
              kind: 'title',
              text: 'NO GOOD!',
              tone: 'red',
              // Plain words, not "CONVERSION FAILED": newcomers know the play as the extra point / the 2-point play.
              sub: hud?.tryPlay === 'two_point' ? 'NO 2 POINTS' : 'EXTRA POINT MISSED',
              size: 'pop',
              durationMs: 1500,
            },
      ];
    case 'INTERCEPTION':
      return [
        {
          kind: 'title',
          text: 'PICKED OFF!',
          tone: 'cyan',
          sub: `${name(e.actor)} READS IT`,
          durationMs: 2000,
          ...head(ctx, e.actor, 'determined'),
        },
      ];
    case 'SACK':
      // Worded from the sacker's side (the face on the band is his): "THE ROOKIE SACKS BIG TOM · LOSS OF 3". A
      // sack is a tackle behind the line, so it always loses at least a yard (never "NO GAIN", QA r3).
      return [
        {
          kind: 'title',
          text: 'SACK!',
          tone: 'red',
          sub: sackSub(name(e.actor), name(other(side(e.actor))), e.yards),
          durationMs: 1800,
          ...head(ctx, e.actor, 'determined'),
        },
      ];
    case 'OFFSIDE':
      return [
        {
          kind: 'title',
          text: 'OFFSIDE',
          tone: 'gold',
          sub: offsideSub(ctx, e.actor, e.penaltyYards),
          durationMs: 2200,
          ...head(ctx, e.actor, 'shocked'),
        },
      ];
    case 'SAFETY':
      return [
        {
          kind: 'title',
          text: 'SAFETY!',
          tone: 'red',
          sub: `+2 · ${scoreLine(e.score)}`,
          stamp: '+2',
          durationMs: 2000,
          ...head(ctx, e.actor, 'celebrating'),
        },
      ];
    case 'TURNOVER_ON_DOWNS':
      return [{ kind: 'title', text: 'TURNOVER ON DOWNS', tone: 'red', durationMs: 1800, size: 'big' }];
    case 'FAKE_SUCCESS':
      // Mid-play hype is a compact pop (like TRUCKED!), never the full-width band: the jumbotron and the booth
      // already call it, and the carrier is still running (QA r3: the band covered the top third mid-chase). The
      // head rides along for the TOP PLAYS card (`isHighlight` keeps fakes).
      return [
        {
          kind: 'title',
          text: 'WHAT A FAKE!',
          tone: 'pink',
          sub: `${BAIT_NAMES[e.bait] ?? 'FEINT'} · +15 POWER`,
          size: 'pop',
          durationMs: 1300,
          ...head(ctx, e.actor, 'celebrating'),
        },
      ];
    case 'TRUCK_BREAK':
      return [{ kind: 'title', text: 'TRUCKED!', tone: 'fire', size: 'pop', durationMs: 1100 }];
    case 'HURDLE_CLEAR':
      return [{ kind: 'title', text: 'HURDLED!', tone: 'lime', size: 'pop', durationMs: 1100 }];
    case 'BULLDOZE':
      return [{ kind: 'title', text: 'BULLDOZED!', tone: 'fire', size: 'pop', durationMs: 1200 }];
    case 'SPIN_DODGE':
      return [{ kind: 'title', text: 'SPUN AWAY!', tone: 'cyan', size: 'pop', durationMs: 1000 }];
    case 'SWAT':
      return [{ kind: 'title', text: 'SWATTED!', tone: 'blue', size: 'pop', durationMs: 1000 }];
    case 'SPECIAL_TELEGRAPH': {
      const info = specialInfo(e.special);
      if (!info.key || info.key === 'none') return [];
      return [{ kind: 'title', text: `${info.name}!`, tone: 'pink', size: 'pop', durationMs: 1000 }];
    }
    case 'BIG_PLAY':
      return [{ kind: 'lower', title: 'BIG PLAY!', sub: `${Math.round(e.yards)} YARDS`, tone: 'fire' }];
    case 'FIRST_DOWN':
      return [
        { kind: 'pulse', pulse: 'first_down', player: -1, text: firstDownFlashSub(e.los, e.actor, hud) },
      ];
    case 'RUSH_GO':
      // An interception during the count ends it too: PICKED OFF! says it all, no GO! burst.
      return e.reason === 'turnover' ? [] : [{ kind: 'pulse', pulse: 'go', player: -1 }];
    case 'STAMINA_DENIED':
      return [{ kind: 'pulse', pulse: 'stamina_denied', player: e.actor }];
    case 'POWER_FULL':
      return [{ kind: 'pulse', pulse: 'power_full', player: e.actor }];
    case 'PLAY_END':
      if (e.result === PlayResultKind.TACKLE)
        return [
          { kind: 'lower', title: yardageText(Math.round(e.yards)), tone: e.yards > 0 ? 'lime' : 'red' },
        ];
      if (e.result === PlayResultKind.INCOMPLETE)
        return [{ kind: 'lower', title: 'INCOMPLETE', sub: 'THAT TRY IS USED', tone: 'red' }];
      return [];
    case 'DRIVE_START': {
      const per = hud?.drivesPerPlayer;
      // Classic plays the clock: possessions are not counted against a number of drives.
      const classic = hud?.format === MatchFormat.CLASSIC;
      return [
        {
          kind: 'lower',
          title: `${name(e.actor)} BALL`,
          sub: e.overtime
            ? 'OVERTIME POSSESSION'
            : classic
              ? halfLabel(hud?.half ?? 1)
              : per
                ? `DRIVE ${e.driveNumber} OF ${per}`
                : `DRIVE ${e.driveNumber}`,
          tone: e.actor === 1 ? 'blue' : 'red',
        },
      ];
    }
    case 'OVERTIME_START':
      return [
        {
          kind: 'title',
          text: 'OVERTIME!',
          tone: 'cyan',
          sub: otSub(e.round),
          durationMs: 2400,
        },
      ];
    case 'GAME_OVER':
      return e.actor === 0 || e.actor === 1
        ? [
            {
              kind: 'title',
              text: 'FINAL!',
              tone: 'gold',
              sub: `${name(e.actor)} WINS ${scoreLine(e.score)}`,
              durationMs: 3200,
              ...head(ctx, e.actor, 'celebrating'),
            },
          ]
        : [];
    default:
      return classicMoments(e, ctx);
  }
};

/** Classic-format events, matched by name and read loosely (the sim may or may not emit them). */
interface LooseClassic {
  type: string;
  actor: number;
  kind?: string;
  downed?: boolean;
  half?: number;
  safetyKick?: boolean;
  score?: readonly number[];
}

const KICK_NAMES: Record<string, string> = { kickoff: 'KICKOFF', onside: 'ONSIDE KICK', punt: 'PUNT' };

export const classicMoments = (ev: SimEvent, ctx: MomentContext): Moment[] => {
  const e = ev as unknown as LooseClassic;
  const name = (p: number): string => ctx.names[side(p)];
  switch (e.type) {
    case 'KICKOFF_START':
      return [
        {
          kind: 'lower',
          title: e.safetyKick ? 'FREE KICK' : 'KICKOFF',
          sub: `${name(e.actor)} KICKS${e.half === 2 ? ' · 2ND HALF' : ''}`,
          tone: e.actor === 1 ? 'blue' : 'red',
        },
      ];
    case 'KICK':
      return e.kind === 'onside'
        ? [{ kind: 'title', text: 'ONSIDE KICK!', tone: 'pink', size: 'pop', durationMs: 1200 }]
        : e.kind === 'punt'
          ? [{ kind: 'title', text: 'PUNT!', tone: 'lime', size: 'pop', durationMs: 900 }]
          : [];
    case 'KICK_RECOVERED':
      if (e.downed) return [{ kind: 'lower', title: 'DOWNED', sub: 'BALL IS DEAD', tone: 'gold' }];
      return [
        {
          kind: 'title',
          text: e.kind === 'onside' ? 'ONSIDE RECOVERED!' : 'RECOVERED!',
          tone: 'lime',
          sub: `${name(e.actor)} KEEPS IT`,
          durationMs: 2000,
          ...head(ctx, e.actor, 'celebrating'),
        },
      ];
    case 'TOUCHBACK':
      return [{ kind: 'lower', title: 'TOUCHBACK', sub: `${name(e.actor)} BALL`, tone: 'gold' }];
    case 'HALF_END':
      return e.half === 1
        ? [
            {
              kind: 'title',
              text: 'HALFTIME',
              tone: 'cyan',
              sub: `${scoreLine(e.score ?? [0, 0])} · 2ND HALF KICKOFF NEXT`,
              durationMs: 2800,
            },
          ]
        : [];
    default:
      return [];
  }
};

/** Kick-play name for a KICK event payload (HUD prompts, tests). */
export const kickName = (kind: string | undefined): string => KICK_NAMES[kind ?? ''] ?? 'KICK';

/** Fallback: moments from a play result read off the HUD (used only when no events are ingested). */
export const momentsFromResult = (h: HudSnapshot, ctx: MomentContext): Moment[] => {
  const r = h.lastResult;
  const scorer = r.scorer === 0 || r.scorer === 1 ? r.scorer : h.offense;
  const name = (p: number): string => ctx.names[side(p)];
  const score = scoreLine(h.score);
  switch (r.kind) {
    case PlayResultKind.TOUCHDOWN:
      return [
        {
          kind: 'title',
          text: 'TOUCHDOWN!!',
          tone: 'gold',
          sub: `${name(scorer)} +6 · ${score}`,
          stamp: '+6',
          durationMs: 2600,
          ...head(ctx, scorer, 'celebrating'),
        },
      ];
    case PlayResultKind.PICK_SIX:
      return [
        {
          kind: 'title',
          text: 'PICK SIX!!',
          tone: 'cyan',
          sub: `${name(scorer)} +6 · ${score}`,
          stamp: '+6',
          durationMs: 2600,
          ...head(ctx, scorer, 'celebrating'),
        },
      ];
    case PlayResultKind.FIELD_GOAL_GOOD:
      return [
        {
          kind: 'title',
          text: 'FIELD GOAL IS GOOD!',
          tone: 'gold',
          sub: `+3 · ${score}`,
          stamp: '+3',
          durationMs: 2200,
          ...head(ctx, scorer, 'celebrating'),
        },
      ];
    case PlayResultKind.FIELD_GOAL_MISS:
      return [
        {
          kind: 'title',
          text: 'NO GOOD!',
          tone: 'red',
          durationMs: 1900,
          ...head(ctx, h.offense, 'shocked'),
        },
      ];
    case PlayResultKind.INTERCEPTION:
      return [
        {
          kind: 'title',
          text: 'PICKED OFF!',
          tone: 'cyan',
          sub: `${name(other(h.offense))} READS IT`,
          durationMs: 2000,
          ...head(ctx, other(h.offense), 'determined'),
        },
      ];
    case PlayResultKind.SACK:
      return [
        {
          kind: 'title',
          text: 'SACK!',
          tone: 'red',
          sub: sackSub(name(other(h.offense)), name(h.offense), r.yards),
          durationMs: 1800,
          ...head(ctx, other(h.offense), 'determined'),
        },
      ];
    case PlayResultKind.OFFSIDE:
      return [
        {
          kind: 'title',
          text: 'OFFSIDE',
          tone: 'gold',
          sub: offsideSub(ctx, other(h.offense), null),
          durationMs: 2200,
          ...head(ctx, other(h.offense), 'shocked'),
        },
      ];
    case PlayResultKind.SAFETY:
      return [
        { kind: 'title', text: 'SAFETY!', tone: 'red', sub: `+2 · ${score}`, stamp: '+2', durationMs: 2000 },
      ];
    case PlayResultKind.TURNOVER_ON_DOWNS:
      return [{ kind: 'title', text: 'TURNOVER ON DOWNS', tone: 'red', durationMs: 1800 }];
    case PlayResultKind.CONVERSION_GOOD:
      return [
        {
          kind: 'title',
          text: r.points >= 2 ? '2-POINT GOOD!' : 'EXTRA POINT GOOD!',
          tone: 'gold',
          sub: `+${r.points} · ${score}`,
          size: 'pop',
          durationMs: 1700,
        },
      ];
    case PlayResultKind.CONVERSION_FAILED:
      return [
        {
          kind: 'title',
          text: 'NO GOOD!',
          tone: 'red',
          sub: 'NO EXTRA POINTS',
          size: 'pop',
          durationMs: 1500,
        },
      ];
    case PlayResultKind.TACKLE:
      return [{ kind: 'lower', title: yardageText(r.yards), tone: r.yards > 0 ? 'lime' : 'red' }];
    case PlayResultKind.INCOMPLETE:
      return [{ kind: 'lower', title: 'INCOMPLETE', sub: 'THAT TRY IS USED', tone: 'red' }];
    default:
      return [];
  }
};

const isDeadPhase = (p: number): boolean =>
  p === Phase.DEAD || p === Phase.CONVERSION || p === Phase.GAME_OVER;

/**
 * HUD-transition moments. Always: the GO! burst when the rush count ends. Only without events: play results on
 * the transition into a dead ball, 1ST DOWN from `lastResult.firstDown`, OVERTIME and FINAL.
 */
export const momentsFromHud = (
  prev: HudSnapshot | null,
  next: HudSnapshot,
  ctx: MomentContext,
  eventsWired: boolean,
): Moment[] => {
  const out: Moment[] = [];
  if (!eventsWired && prev && prev.phase === Phase.RUSH_COUNT && next.phase === Phase.LIVE)
    out.push({ kind: 'pulse', pulse: 'go', player: -1 });
  if (eventsWired || !prev) return out;
  if (!isDeadPhase(prev.phase) && isDeadPhase(next.phase) && next.lastResult.kind !== PlayResultKind.NONE) {
    out.push(...momentsFromResult(next, ctx));
    if (next.lastResult.firstDown) out.push({ kind: 'pulse', pulse: 'first_down', player: -1 });
  }
  if (!prev.overtime && next.overtime)
    out.push({
      kind: 'title',
      text: 'OVERTIME!',
      tone: 'cyan',
      sub: otSub(next.otRound),
      durationMs: 2400,
    });
  if (
    prev.phase !== Phase.GAME_OVER &&
    next.phase === Phase.GAME_OVER &&
    (next.winner === 0 || next.winner === 1)
  )
    out.push({
      kind: 'title',
      text: 'FINAL!',
      tone: 'gold',
      sub: `${ctx.names[side(next.winner)]} WINS ${scoreLine(next.score)}`,
      durationMs: 3200,
      ...head(ctx, next.winner, 'celebrating'),
    });
  return out;
};

/** A big broadcast moment kept for the Results screen's "TOP PLAYS" reel. */
export interface Highlight {
  text: string;
  sub?: string;
  tone: SuperTitleTone;
  character?: string;
  expression?: string;
  side?: 0 | 1;
  /** Seconds since the match started (shown as the replay timecode). */
  atSec: number;
}

/** Highlights of the current/last match (reset when the next match starts). */
export const highlights = signal<Highlight[]>([]);
let matchStartMs = 0;
const nowMs = (): number => (typeof performance !== 'undefined' ? performance.now() : Date.now());

/** Titles worth a replay card (not the FINAL banner, not small pops). */
const isHighlight = (m: Extract<Moment, { kind: 'title' }>): boolean =>
  ((m.size ?? 'big') === 'big' || m.text === 'WHAT A FAKE!') &&
  m.text !== 'FINAL!' &&
  m.text !== 'OVERTIME!' &&
  m.text !== 'TURNOVER ON DOWNS';

export const recordHighlight = (m: Extract<Moment, { kind: 'title' }>): void => {
  if (!isHighlight(m)) return;
  const h: Highlight = {
    text: m.text,
    tone: m.tone,
    atSec: Math.max(0, Math.round((nowMs() - matchStartMs) / 1000)),
    ...(m.sub ? { sub: m.sub } : {}),
    ...(m.character ? { character: m.character } : {}),
    ...(m.expression ? { expression: m.expression } : {}),
    ...(m.side !== undefined ? { side: m.side } : {}),
  };
  highlights.value = [...highlights.value, h].slice(-12);
};

/** Pick the best `n` highlights for the results reel: scores first, then takeaways/fakes, latest first. */
export const topHighlights = (all: readonly Highlight[], n = 3): Highlight[] => {
  const rank = (h: Highlight): number =>
    /TOUCHDOWN|PICK SIX/.test(h.text) ? 0 : /PICKED|SACK|FAKE|SAFETY/.test(h.text) ? 1 : 2;
  return all
    .map((h, i) => ({ h, i }))
    .sort((a, b) => rank(a.h) - rank(b.h) || b.i - a.i)
    .slice(0, n)
    .sort((a, b) => a.i - b.i)
    .map((x) => x.h);
};

/** A play-result yardage lower-third ("GAIN OF 4", "LOSS OF 2", "NO GAIN"). */
const isYardageLower = (m: Moment): boolean =>
  m.kind === 'lower' && /^(GAIN OF|LOSS OF|NO GAIN)/.test(m.title);

/**
 * One frame's moments, de-conflicted: the bottom-left slot shows ONE card. A first down's 1ST DOWN! flash (the
 * chyron) wins over that play's yardage lower-third, which would otherwise sit on top of it for the whole flash.
 */
export const resolveMoments = (moments: readonly Moment[]): Moment[] =>
  moments.some((m) => m.kind === 'pulse' && m.pulse === 'first_down')
    ? moments.filter((m) => !isYardageLower(m))
    : [...moments];

export const applyMoments = (store: UiStore, input: readonly Moment[]): void => {
  const moments = resolveMoments(input);
  for (const m of moments) {
    if (m.kind === 'title') {
      recordHighlight(m);
      const { kind: _k, text, tone, sub, durationMs, ...extras } = m;
      store.showSuperTitle(text, tone, sub, durationMs ?? 1600, extras);
    } else if (m.kind === 'lower') store.showLowerThird(m.title, m.sub, m.tone);
    else store.pulseHud(m.pulse, m.player, m.text);
  }
};

/**
 * Subscribe the moment pipeline to the store. Events (when the match controller forwards them) win; the HUD
 * fallback switches itself off for the rest of the match as soon as one `ingestEvents` call arrives.
 */
export const createMomentTracker = (store: UiStore): (() => void) => {
  let eventsWired = false;
  let prevHud: HudSnapshot | null = null;
  const offEvents = store.onSimEvents((events) => {
    eventsWired = true;
    if (events.length === 0) return;
    const ctx = contextFrom(store.matchSetup.peek(), store.hud.peek());
    const hud = store.hud.peek();
    applyMoments(
      store,
      events.flatMap((e) => momentsFromEvent(e, ctx, hud)),
    );
  });
  const offHud = effect(() => {
    const h = store.hud.value;
    if (!h) {
      // Match ended / left: reset for the next match.
      prevHud = null;
      eventsWired = false;
      return;
    }
    if (!prevHud) {
      highlights.value = [];
      matchStartMs = nowMs();
    }
    const ctx = contextFrom(store.matchSetup.peek(), h);
    applyMoments(store, momentsFromHud(prevHud, h, ctx, eventsWired));
    prevHud = h;
  });
  return () => {
    offEvents();
    offHud();
  };
};
